import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useEntity } from '@app/hooks/useEntity';
import { COURSE_STATUS } from '@app/model/Course';
import { useCourseStore } from '@app/store/entityStore';
import { ddMMyyyyFormat } from '@app/utils/functions';
import { ActionList, ActionListItem, Badge, Bullseye, Button, EmptyState, EmptyStateActions, EmptyStateBody, EmptyStateFooter, Icon, Label, MenuToggle, MenuToggleElement, PageSection, Pagination, PaginationVariant, SearchInput, Select, SelectOption, Split, SplitItem, Title, Toolbar, ToolbarContent, ToolbarFilter, ToolbarGroup, ToolbarItem, ToolbarLabel, ToolbarLabelGroup, ToolbarToggleGroup, Tooltip } from '@patternfly/react-core';
import { FilterIcon, PencilAltIcon, SearchIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { DataLoadErrorRow } from '@app/components/DataLoadError';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useFilterRestore } from '@app/hooks/useFilterRestore';
import { useRowRestore } from '@app/hooks/useRowRestore';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { hasRealmRole } from '@app/utils/keycloak';
import { ROLE } from '@app/utils/roles';

const emptyFilters = (): Record<string, string[]> => ({ name: [], status: [] });

/** What comes back when the user returns from a course's detail page. */
interface CoursesView {
  filters: Record<string, string[]>;
  category: string | number | undefined;
  sortIndex: number | null;
  sortDirection: 'asc' | 'desc' | null;
}

const Courses: React.FunctionComponent = () => {
  const navigate = useNavigate();

  // --- Core State ---
  const { items: rows, error, retry } = useEntity(useCourseStore);

  /**
   * A course is everybody's to read and an administrator's to write, so the list is the same
   * list for everyone and only the way into a writable form differs. /courses/new is guarded on
   * the route as well, so the button is not the thing holding anybody back - see
   * README-permissions.md.
   */
  const isAdmin = hasRealmRole(ROLE.ADMIN);

  // --- UI State ---
  // The filters last applied here, kept only for the trip out to a course and back.
  const { restored, keep } = useFilterRestore<CoursesView>('courses');
  const { restoredPage, rememberRow, rowProps } = useRowRestore('courses');
  const [page, setPage] = React.useState(restoredPage ?? 1);
  const { perPage, setPerPage } = useTablePreferences('courses', 10);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = React.useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = React.useState(false);
  const [currentCategory, setCurrentCategory] = React.useState<string | number | undefined>(restored?.category ?? 'Name');

  // --- Sorting State ---
  // Restored along with the filters: the page number comes back too, and on a differently
  // sorted list that page holds different rows.
  const [activeSortIndex, setActiveSortIndex] = React.useState<number | null>(restored?.sortIndex ?? null);
  const [activeSortDirection, setActiveSortDirection] = React.useState<'asc' | 'desc' | null>(restored?.sortDirection ?? null);

  // --- Filter State ---
  const [filters, setFilters] = React.useState<Record<string, string[]>>(restored?.filters ?? emptyFilters());
  const [inputValue, setInputValue] = React.useState('');

  React.useEffect(() => {
    keep({ filters, category: currentCategory, sortIndex: activeSortIndex, sortDirection: activeSortDirection });
  }, [filters, currentCategory, activeSortIndex, activeSortDirection, keep]);

  // --- Dynamic Search Wrapper ---
  const applyFrontendFilters = (newFilters: Record<string, string[]>) => {
    beginLoading();
    setTimeout(() => {
      setFilters(newFilters);
      setPage(1);
      requestAnimationFrame(() => setTimeout(() => endLoading(), 0));
    }, 10);
  };

  // --- Derived State 1: Filtering ---
  const filteredRows = React.useMemo(() => {
    return rows.filter((row) => {
      const matchesName = filters.name?.length === 0 || filters.name?.some((name) => 
        row.sku.toLowerCase().includes(name.toLowerCase()) || 
        row.name.toLowerCase().includes(name.toLowerCase())
      );
      const matchesStatus = filters.status?.length === 0 || filters.status?.includes(row.status);
      
      return matchesName && matchesStatus;
    });
  }, [rows, filters]);

  // --- Derived State 2: Sorting ---
  const sortedRows = React.useMemo(() => {
    if (activeSortIndex === null || activeSortDirection === null) return filteredRows;
    
    return [...filteredRows].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (activeSortIndex) {
        case 0: aValue = a.sku?.toLowerCase() || ''; bValue = b.sku?.toLowerCase() || ''; break;
        case 1: aValue = a.name?.toLowerCase() || ''; bValue = b.name?.toLowerCase() || ''; break;
        case 2: aValue = a.curriculum?.name?.toLowerCase() || ''; bValue = b.curriculum?.name?.toLowerCase() || ''; break;
        case 3: aValue = a.status || ''; bValue = b.status || ''; break;
        case 4: 
          aValue = a.latestVersion?.version || ''; 
          bValue = b.latestVersion?.version || ''; 
          break;
        case 5:
          aValue = a.latestVersion?.duration ?? 0;
          bValue = b.latestVersion?.duration ?? 0;
          break;
        case 6: {
          const aDate = a.latestVersion?.releaseDate;
          const bDate = b.latestVersion?.releaseDate;
          aValue = aDate ? new Date(aDate).getTime() : 0;
          bValue = bDate ? new Date(bDate).getTime() : 0;
          break;
        }
      }

      if (aValue < bValue) return activeSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return activeSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRows, activeSortIndex, activeSortDirection]);

  // --- Derived State 3: Pagination ---
  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return sortedRows.slice(startIdx, startIdx + perPage);
  }, [sortedRows, page, perPage]);

  // --- Event Handlers ---
  const onDeleteFilter = (category: string | ToolbarLabelGroup, id: string | ToolbarLabel) => {
    const key = String(category).toLowerCase();
    if (filters[key]) {
      applyFrontendFilters({ ...filters, [key]: filters[key].filter(fil => fil !== id) });
    } else {
      applyFrontendFilters(emptyFilters());
    }
  };

  const clearFilters = () => applyFrontendFilters(emptyFilters());

  const onTextSearch = (value: string) => {
    if (!value.trim()) return;
    const prevFilters = filters.name || [];
    if (!prevFilters.includes(value.trim())) {
      applyFrontendFilters({ ...filters, name: [...prevFilters, value.trim()] });
      setInputValue('');
    }
  };

  const onStatusSelect = (event?: React.MouseEvent<Element, MouseEvent>, selection?: string | number) => {
    if (!event) return;
    const checked = (event.target as HTMLInputElement).checked;
    const currentStatus = filters.status || [];
    const newStatus = checked ? [...currentStatus, String(selection)] : currentStatus.filter(v => v !== String(selection));
    applyFrontendFilters({ ...filters, status: newStatus });
  };

  const getSortParams = (columnIndex: number) => ({
    sortBy: { index: activeSortIndex !== null ? activeSortIndex : undefined, direction: activeSortDirection !== null ? activeSortDirection : undefined, defaultDirection: 'asc' as const },
    onSort: (_event: React.MouseEvent, index: number, direction: 'asc' | 'desc') => { setActiveSortIndex(index); setActiveSortDirection(direction); },
    columnIndex
  });

  // Course removal is not wired up yet; restore this with the <RemoveButton /> commented
  // out in the Actions cell below.
  // const onRemoveConfirm = (id: string | number) => {
  //   // API logic for delete goes here
  // };

  // --- Render Helpers ---
  const buildCategoryDropdown = () => (
    <ToolbarItem>
      <Select
        onSelect={(e, val) => { setCurrentCategory(val); setIsCategoryDropdownOpen(false); setInputValue(''); }}
        selected={currentCategory}
        isOpen={isCategoryDropdownOpen}
        onOpenChange={(isOpen) => setIsCategoryDropdownOpen(isOpen)}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)} isExpanded={isCategoryDropdownOpen} icon={<FilterIcon />} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            {currentCategory}
          </MenuToggle>
        )}
      >
        <SelectOption value="Name">Name</SelectOption>
        <SelectOption value="Status">Status</SelectOption>
      </Select>
    </ToolbarItem>
  );

  const buildFilterDropdown = () => {
    const statusMenuItems = Object.values(COURSE_STATUS).map(status => (
      <SelectOption hasCheckbox key={status} value={status} isSelected={filters.status?.includes(status)}>
        {status.replace(/_/g, ' ')}
      </SelectOption>
    ));

    return (
      <React.Fragment>
        {currentCategory === 'Name' && (
          <ToolbarFilter labels={filters.name || []} deleteLabel={onDeleteFilter} categoryName="Name" showToolbarItem={true}>
            <SearchInput aria-label="name filter" placeholder="Filter by name or SKU..." onChange={(_event, value) => setInputValue(value)} value={inputValue} onClear={() => setInputValue('')} onSearch={(_e, val) => onTextSearch(val)} />
          </ToolbarFilter>
        )}

        <ToolbarFilter labels={filters.status} deleteLabel={onDeleteFilter} categoryName="Status" showToolbarItem={currentCategory === 'Status'}>
          <Select aria-label="Status" isOpen={isFilterDropdownOpen} onOpenChange={(isOpen) => setIsFilterDropdownOpen(isOpen)} popperProps={{ minWidth: '100px' }} onSelect={onStatusSelect} selected={filters.status} toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)} isExpanded={isFilterDropdownOpen} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
                Filter by status {filters.status && filters.status.length > 0 && <Badge isRead>{filters.status.length}</Badge>}
              </MenuToggle>
            )}>
            {statusMenuItems}
          </Select>
        </ToolbarFilter>
      </React.Fragment>
    );
  };

  const buildPagination = (variant: 'bottom' | 'top', isCompact: boolean) => (
    <Pagination
      isCompact={isCompact} itemCount={filteredRows.length} page={page} perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant} isStatic
    />
  );

  const renderLabel = (labelText: string) => {
    switch (labelText) {
      case 'ACTIVE': return <Label color="green">Active</Label>;
      case 'LEGACY': return <Label color="orange">Legacy</Label>;
      case 'COMING_SOON': return <Label color="blue">Coming Soon</Label>;
      case 'RETIRED': return <Label color="red">Retired</Label>;
      case 'RE_SKUED': return <Label color="blue">RE SKUED</Label>;
      default: return <>{labelText}</>;
    }
  };

  return (
    <React.Fragment>
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Courses</Title>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              {isAdmin && (
                <ActionListItem>
                  <Button variant="primary" onClick={() => navigate('/courses/new')}>New Course</Button>
                </ActionListItem>
              )}
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      <Toolbar id="toolbar-with-chip-groups" clearAllFilters={clearFilters} collapseListedFiltersBreakpoint="xl">
        <ToolbarContent>
          <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
            <ToolbarGroup variant="filter-group" style={{ lineHeight: '22px', alignItems: 'center' }}>
              {buildCategoryDropdown()}
              {buildFilterDropdown()}
            </ToolbarGroup>
          </ToolbarToggleGroup>
          <ToolbarItem variant="pagination">{buildPagination('top', true)}</ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
          gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
      <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset))', minHeight: 0 }}>
        <InnerScrollContainer>
          <Table aria-label="Courses" isStickyHeader variant='compact' gridBreakPoint="">
            <Thead>
              <Tr>
                <Th sort={getSortParams(0)}>SKU</Th>
                <Th sort={getSortParams(1)}>Name</Th>
                <Th sort={getSortParams(2)}>Curriculum</Th>
                <Th sort={getSortParams(3)}>Status</Th>
                <Th sort={getSortParams(4)}>Latest Version</Th>
                <Th sort={getSortParams(5)}>Duration</Th>
                <Th sort={getSortParams(6)}>Last Update</Th>
                <Th width={10} isStickyColumn stickyMinWidth="90px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedRows.map((row) => (
                <Tr key={row.id} {...rowProps(row.id)}>
                  <Td dataLabel="SKU"><b>{row.sku}</b></Td>
                  <Td dataLabel="Name">{row.name}</Td>
                  <Td dataLabel="Curriculum">{row.curriculum?.name}</Td>
                  <Td dataLabel="Status">{renderLabel(row.status)}</Td>
                  <Td dataLabel="Latest Version">{row.latestVersion?.version}</Td>
                  <Td dataLabel="Duration">{row.latestVersion?.duration ?? ''}</Td>
                  <Td dataLabel="Last Update">{ddMMyyyyFormat(row.latestVersion?.releaseDate)}</Td>
                  <Td dataLabel="Actions" modifier="nowrap"
                    isStickyColumn stickyMinWidth="90px" stickyRightOffset="0" hasLeftBorder>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                      <Tooltip content={isAdmin ? 'Edit' : 'View'}>
                        <Button icon={<Icon size='lg'><PencilAltIcon /></Icon>} 
                          variant="plain" aria-label={`${isAdmin ? 'Edit' : 'View'} ${row.sku}`}
                          onClick={() => { rememberRow(row.id, page); navigate('/courses/' + row.sku); }}
                          className="compactButtonStyle"
                        />
                      </Tooltip>
                      { /* <RemoveButton confirmationMessage='Confirm course removal?' onRemoveConfirm={(ev) => onRemoveConfirm(row.id, ev)} /> */}
                    </div>
                  </Td>
                </Tr>
              ))}
              {/* Nothing to show: distinguish "the load failed" from "no records match" */}
              {error && filteredRows.length === 0 && (
                <DataLoadErrorRow colSpan={8} error={error} entityLabel="courses" onRetry={retry} />
              )}
              {!error && filteredRows.length === 0 && (
                <Tr>
                  <Td colSpan={8}>
                    <Bullseye>
                      <EmptyState headingLevel="h5" icon={SearchIcon} titleText="Clear all filters and try again.">
                        <EmptyStateBody>No results match the filter criteria. Clear all filters and try again.</EmptyStateBody>
                        <EmptyStateFooter>
                          <EmptyStateActions><Button variant="link" onClick={clearFilters}>Clear all filters</Button></EmptyStateActions>
                        </EmptyStateFooter>
                      </EmptyState>
                    </Bullseye>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </InnerScrollContainer>
      </OuterScrollContainer>
      {buildPagination('bottom', false)}
    </React.Fragment>
  );
};

export { Courses };
